"use client";

import Image from "next/image";
import Link from "next/link";
import { Anchor, Breadcrumb, Typography } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { extractHeadings, renderMarkdownWithIds } from "../posts";
import BuyingGuideCard from "./BuyingGuideCard";
import ExpertCard from "./ExpertCard";
import MostReadArticles from "./MostReadArticles";
import ReadingProgress from "./ReadingProgress";
import SocialShareButtons from "./SocialShareButtons";
import RelatedArticles from "./RelatedArticles";

const { Title, Paragraph, Text } = Typography;

const heroWrapperStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 32,
  marginBottom: 32,
  alignItems: "stretch",
};

const heroImageStyle = {
  position: "relative",
  flex: "0 0 320px",
  minWidth: 260,
  height: 260,
  borderRadius: 24,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.1)",
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e4e9f2",
  borderRadius: 18,
  padding: "28px 32px",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.07)",
};

const primaryCtaStyle = {
  background: "var(--brand-gradient)",
  color: "#fff",
  padding: "12px 20px",
  borderRadius: 12,
  fontWeight: 600,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 8px 20px rgba(130, 0, 244, 0.25)",
};

const secondaryCtaStyle = {
  padding: "12px 20px",
  borderRadius: 12,
  fontWeight: 600,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #d0d5dd",
  color: "#475467",
  background: "#fff",
};

const metaBadgeStyle = {
  background: "#f3ebff",
  color: "#6b00d4",
  padding: "4px 12px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 13,
};

export default function BlogPostContent({ post }) {
  if (!post) return null;

  // Strip any leading markdown h1 (# Title) to avoid duplicate h1 — the
  // component already renders the post title as <Title level={1}>.
  const rawContent = (post.content || "").replace(/^\s*#\s+[^\n]+\n*/, "");
  let html = renderMarkdownWithIds(rawContent);
  html = html.replace(/<a (?![^>]*target=)[^>]*href=["'](https?:\/\/[^"']+)["'][^>]*>/g, (match) => {
    if (/rel=/.test(match)) {
      return match
        .replace(/rel=["']([^"']*)["']/, (relMatch, relVal) => {
          let newRel = relVal;
          if (!/noopener/.test(newRel)) newRel += " noopener";
          if (!/dofollow/.test(newRel)) newRel += " dofollow";
          return `rel="${newRel.trim()}"`;
        })
        .replace('>', ' target="_blank">');
    }
    return match.replace('>', ' target="_blank" rel="noopener dofollow">');
  });

  const headings = extractHeadings(post.content || "");
  const tocItems = headings
    .filter((h) => h.level === 2)
    .map((h, index) => ({
      key: h.id,
      href: `#${h.id}`,
      title: (
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", color: "#101828" }}>
          <Text type="secondary" style={{ fontSize: 13 }}>{`${index + 1}.`}</Text>
          <span>{h.text}</span>
        </span>
      ),
    }));

  const formattedDate = new Date(post.date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const authorInitials = post.author?.name
    ? post.author.name
        .split(/\s+/)
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "IA";

  return (
    <section>
      <ReadingProgress />
      <Breadcrumb style={{ marginBottom: 24 }}>
        <Breadcrumb.Item>
          <Link href="/">
            <HomeOutlined /> Home
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link href="/blog">Blog</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{post.title}</Breadcrumb.Item>
      </Breadcrumb>

      <div style={heroWrapperStyle}>
        <div style={{ flex: "1 1 400px", minWidth: 280 }}>
          <Title level={1} style={{ fontSize: 42, marginBottom: 16 }}>
            {post.title}
          </Title>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              color: "#475467",
              fontSize: 15,
              marginBottom: 20,
            }}
          >
            <time dateTime={post.date} style={{ fontWeight: 600 }}>
              {formattedDate}
            </time>
            <span style={{ color: "#d0d5dd" }}>•</span>
            <span style={{ fontWeight: 500 }}>{post.readingTime}</span>
            {post.category && <span style={metaBadgeStyle}>{post.category}</span>}
          </div>
          {post.author && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 16,
                padding: "14px 18px",
                borderRadius: 16,
                background: "#fff",
                border: "1px solid #e4e9f2",
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "#ececff",
                  color: "#4b008f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                {authorInitials}
              </div>
              <div>
                <Text strong>By {post.author.name}</Text>
                <Paragraph style={{ marginBottom: 0 }}>{post.author.role}</Paragraph>
              </div>
            </div>
          )}
          <Paragraph style={{ fontSize: 17, lineHeight: 1.7, color: "#475467" }}>{post.description}</Paragraph>
        </div>
        <div style={heroImageStyle}>
          <Image
            src={post.image}
            alt={post.title}
            fill
            sizes="(max-width: 1024px) 100vw, 360px"
            className="object-cover"
            priority
          />
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 40 }}>
        <Paragraph style={{ fontSize: 16, marginBottom: 8 }}>
          InvoiceAce articles focus on practical, no-fluff guidance so you can create polished invoices without extra tools.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Key takeaway:</Text> {post.summary || post.description}
        </Paragraph>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 32, alignItems: "flex-start" }}>
        <main style={{ flex: "1 1 640px", minWidth: 0 }}>
          <article
            style={{
              background: "#fff",
              border: "1px solid #e4e9f2",
              borderRadius: 18,
              padding: "32px 36px",
              boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
            }}
          >
            <div
              className="prose max-w-none text-lg leading-relaxed"
              dangerouslySetInnerHTML={{ __html: html }}
            />
            <hr style={{ margin: "48px 0", borderColor: "#e4e9f2" }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 32 }}>
              <Link href="/generator" style={primaryCtaStyle}>
                Create an invoice
              </Link>
              <Link href="/faq" style={secondaryCtaStyle}>
                Read FAQ
              </Link>
              <Link href="/terms" style={secondaryCtaStyle}>
                Terms
              </Link>
            </div>
            <SocialShareButtons slug={post.slug} title={post.title} />
          </article>
          <RelatedArticles slug={post.slug} />
        </main>

        <aside
          style={{
            flex: "1 1 280px",
            maxWidth: 320,
            minWidth: 240,
            position: "sticky",
            top: 120,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {tocItems.length > 0 && (
            <div style={{ ...cardStyle, padding: "20px 24px" }}>
              <Title level={4} style={{ marginBottom: 12 }}>
                In this article
              </Title>
              <Paragraph style={{ color: "#475467", marginBottom: 16 }}>
                Jump straight to the section you need.
              </Paragraph>
              <Anchor affix={false} items={tocItems} style={{ width: "100%" }} />
            </div>
          )}
          <div style={{ ...cardStyle, padding: "20px 24px" }}>
            <SocialShareButtons slug={post.slug} title={post.title} />
          </div>
          <BuyingGuideCard />
          <ExpertCard author={post.author} />
          <MostReadArticles limit={3} />
        </aside>
      </div>
    </section>
  );

}
